import { captureException } from "@sentry/browser";

import { RetailStoresWithDistance } from "../models/location";
import { IRetailStoreWithDistance } from "../models/location.interfaces";
import {
    ILatitude,
    ILongitude,
    IProductUUID,
    ISyncStoreID,
} from "../models/nominals";
import { check } from "../models/utils";
import { ajax } from "../utils/ajax";
import { trackEvent } from "../utils/analytics";
import { getPageSetting } from "../utils/settings";

type ITextQuery = {
    // Search location by string
    q: string;
};

type IGeoQuery = {
    // Search location by latitude and longitude
    origLat: ILatitude;
    origLng: ILongitude;
};

type IOtherFilters = {
    // Max distance from search location
    distance?: number;

    // Return store distance to center
    show_distance?: "true" | "false";

    // Filter by store ID
    external_id?: ISyncStoreID;

    // Filter by store branch
    brand?: string;

    // Filter by store category
    category?: string | null;

    // Filter by retailer name
    retailer?: string[];

    // Filter by product availability
    product?: IProductUUID;

    // Max results. Default sot 500.
    limit?: number;

    // Limit by country.
    country?: string;

    // Slot filters
    has_slot_stearns_foster?: string;
    has_slot_tempur?: string;
    has_slot_sealy?: string;
};

export type IQueryParams = (ITextQuery | IGeoQuery) & IOtherFilters;

export enum StoreCategory {
    // TODO(i18n): These user-facing store category labels need to be translatable. Consider moving to a function that returns translated strings: FLAGSHIP: t`Tempur-Pedic Flagship Store`, ELITE: t`Elite`, NOT_ELITE: t`Not Elite`
    FLAGSHIP = "Tempur-Pedic Flagship Store",
    ELITE = "Elite",
    NOT_ELITE = "Not Elite",
}

export const searchStores = async (
    query: IQueryParams,
): Promise<IRetailStoreWithDistance[]> => {
    const apiURL = getPageSetting("retail_locator_api");
    const brand = getPageSetting("retail_locator_brand");
    const country = getPageSetting("retail_locator_country");
    query = {
        brand: brand ?? undefined,
        show_distance: "true",
        distance: 3000,
        country: country ?? undefined,
        ...query,
    };
    return ajax
        .get(`${apiURL}/api/locations/`)
        .set("Accept", "application/json")
        .query(query)
        .then((resp) => {
            const stores = resp.body;
            // Log stores result
            if (stores.length === 0) {
                trackEvent(
                    "RetailLocator",
                    "storesNotFound",
                    "Stores Not Found at Distance",
                    {
                        nonInteraction: true,
                        distance: query.distance,
                    },
                );
            } else if (stores.length >= 500) {
                trackEvent(
                    "RetailLocator",
                    "tooManyStoresFound",
                    "Too Many Stores at Distance",
                    {
                        nonInteraction: true,
                        distance: query.distance,
                    },
                );
            }
            // TODO(i18n): The unit "mi" should be translatable and locale-appropriate (e.g., "km" for metric locales). Use: t`Distance: ${query.distance}mi`
            trackEvent(
                "RetailLocator",
                "numStoresFound",
                `Distance: ${query.distance}mi`,
                {
                    nonInteraction: true,
                    numStores: stores.length,
                },
            );
            // Decode types
            return check(RetailStoresWithDistance.decode(stores));
        })
        .catch((err) => {
            console.log(err);
            captureException(err);
            return [];
        });
};
